import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { partnersApi } from '@/api/partners/partnersApi';
import { Transaction, TransactionFilters, CreateTransactionRequest, UpdateTransactionRequest } from '@/api/partnerTransactions/types';
import { RootState } from '@/app/rootReducer';

interface TransactionsState {
  transactions: Transaction[];
  selectedTransaction: Transaction | null;
  loading: boolean;
  error: string | null;
  filters: TransactionFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

const initialState: TransactionsState = {
  transactions: [],
  selectedTransaction: null,
  loading: false,
  error: null,
  filters: {
    searchTerm: '',
    types: [],
    entityTypes: [],
    quarter: undefined,
    year: undefined,
    startDate: undefined,
    endDate: undefined,
    partnerId: undefined,
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
};

// Async thunks
export const fetchTransactionsAction = createAsyncThunk(
  'transactions/fetchTransactions',
  async ({ partnerId, filters }: { partnerId: string; filters?: TransactionFilters }) => {
    const response = await partnersApi.fetchTransactions(partnerId, filters);
    return response;
  }
);

export const fetchTransactionAction = createAsyncThunk(
  'transactions/fetchTransaction',
  async (transactionId: string) => {
    const response = await partnersApi.fetchTransaction(transactionId);
    return response;
  }
);

export const createTransactionAction = createAsyncThunk(
  'transactions/createTransaction',
  async (data: CreateTransactionRequest) => {
    const response = await partnersApi.createTransaction(data);
    return response;
  }
);

export const updateTransactionAction = createAsyncThunk(
  'transactions/updateTransaction',
  async ({ id, data }: { id: string; data: UpdateTransactionRequest }) => {
    const response = await partnersApi.updateTransaction(id, data);
    return response;
  }
);

export const deleteTransactionAction = createAsyncThunk(
  'transactions/deleteTransaction',
  async (transactionId: string) => {
    await partnersApi.deleteTransaction(transactionId);
    return transactionId;
  }
);

export const exportTransactionsAction = createAsyncThunk(
  'transactions/exportTransactions',
  async ({ partnerId, filters }: { partnerId: string; filters?: TransactionFilters }) => {
    const response = await partnersApi.exportTransactions(partnerId, filters);
    return response;
  }
);

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setSelectedTransaction: (state, action: PayloadAction<Transaction | null>) => {
      state.selectedTransaction = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<TransactionFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch transactions
    builder
      .addCase(fetchTransactionsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.data;
        state.pagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 10,
          total: action.payload.total || 0,
        };
      })
      .addCase(fetchTransactionsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch transactions';
      });

    // Fetch single transaction
    builder
      .addCase(fetchTransactionAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionAction.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTransaction = action.payload;
      })
      .addCase(fetchTransactionAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch transaction';
      });

    // Create transaction
    builder
      .addCase(createTransactionAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransactionAction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = [action.payload, ...state.transactions];
        state.pagination.total += 1;
      })
      .addCase(createTransactionAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create transaction';
      });

    // Update transaction
    builder
      .addCase(updateTransactionAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransactionAction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.transactions.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.transactions[index] = action.payload;
        }
        if (state.selectedTransaction?.id === action.payload.id) {
          state.selectedTransaction = action.payload;
        }
      })
      .addCase(updateTransactionAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update transaction';
      });

    // Delete transaction
    builder
      .addCase(deleteTransactionAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTransactionAction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = state.transactions.filter(t => t.id !== action.payload);
        state.pagination.total -= 1;
        if (state.selectedTransaction?.id === action.payload) {
          state.selectedTransaction = null;
        }
      })
      .addCase(deleteTransactionAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete transaction';
      });

    // Export transactions
    builder
      .addCase(exportTransactionsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportTransactionsAction.fulfilled, (state) => {
        state.loading = false;
        // Export is handled by the API, no state update needed
      })
      .addCase(exportTransactionsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to export transactions';
      });
  },
});

export const { setSelectedTransaction, setFilters, resetFilters, clearError } = transactionsSlice.actions;

// Selectors
export const selectTransactions = (state: RootState) => state.partnerNetwork.transactions.transactions;
export const selectSelectedTransaction = (state: RootState) => state.partnerNetwork.transactions.selectedTransaction;
export const selectTransactionsLoading = (state: RootState) => state.partnerNetwork.transactions.loading;
export const selectTransactionsError = (state: RootState) => state.partnerNetwork.transactions.error;
export const selectTransactionFilters = (state: RootState) => state.partnerNetwork.transactions.filters;
export const selectTransactionsPagination = (state: RootState) => state.partnerNetwork.transactions.pagination;

export default transactionsSlice.reducer;